import React, { createContext, useContext, useState } from 'react';

/**
 * Mount slot for the grid view's Breadcrumbs + CategoryTabs row. The provider
 * renders an empty target div as the first child of the page body; grid views
 * portal their breadcrumbs/tabs into that target so they render *above* any
 * Builder content (banner / Vimeo / etc.) that authors drop above the grid
 * block in the page tree.
 *
 * Refs attached inside the portaled subtree (`breadcrumbsRef`, `numResultsRef`)
 * keep working — `createPortal` preserves the React tree, only the DOM parent
 * changes — so `GridScrollHeader`'s `useIsHiddenBehind` observer continues to
 * track the live bounding rect at the new physical position.
 */
const GridTabsPortalContext = createContext<HTMLElement | null>(null);

export const useGridTabsPortal = (): HTMLElement | null =>
  useContext(GridTabsPortalContext);

export const GridTabsPortalProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // useState (not useRef) so consumers re-render once the target attaches.
  const [target, setTarget] = useState<HTMLElement | null>(null);

  return (
    <>
      <div ref={setTarget} data-grid-tabs-portal="" />
      <GridTabsPortalContext.Provider value={target}>
        {children}
      </GridTabsPortalContext.Provider>
    </>
  );
};
